import moment from 'moment';
import {toDateOnly} from '../../../../../utility/dateUtil';
import {validateNINumber} from '../../../../../utility/validationUtil';

export const MAX_FSM_CONTACTS = 5;

/**
 * Calendar date of birth for FSM eligibility contacts (AD-2588).
 * Moments use the picker's calendar day as UTC midnight; ISO/API values use the
 * Europe/London calendar day so legacy local-midnight→UTC shifts still hydrate
 * as the day staff saw on the profile.
 */
export const toFsmContactDateOnly = (value) => {
    if (value == null || value === '') {
        return null;
    }

    if (moment.isMoment(value)) {
        return toDateOnly(value);
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return null;
    }

    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/London',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(date);

    const year = Number(parts.find((p) => p.type === 'year')?.value);
    const month = Number(parts.find((p) => p.type === 'month')?.value);
    const day = Number(parts.find((p) => p.type === 'day')?.value);
    if (!year || !month || !day) {
        return null;
    }

    return moment.utc([year, month - 1, day]).startOf('day').toISOString();
};

export const fsmContactDobCalendarDay = (value) => {
    const iso = toFsmContactDateOnly(value);
    return iso ? moment.utc(iso).format('YYYY-MM-DD') : '';
};

export const isContactComplete = (contact) => {
    if (!contact) {
        return false;
    }

    const niValidation = validateNINumber(contact.insuranceNumber);
    return !!contact.fullName &&
        !!contact.dateOfBirth &&
        !!contact.insuranceNumber &&
        niValidation.isValid &&
        !contact.fullNameError &&
        !contact.dateOfBirthError &&
        !contact.insuranceNumberError;
};

/**
 * Parent contacts are shown and required only for eligibility checks
 * (consent-to-check / last 6 years). Hidden when currently in receipt.
 */
export const shouldShowFsmContacts = ({lastFree, isNotApplicable}) =>
    !!(lastFree || isNotApplicable);

/** @deprecated Use shouldShowFsmContacts — same rule. */
export const requiresFsmContacts = shouldShowFsmContacts;

export const areFsmContactsValid = ({lastFree, isNotApplicable, contacts}) => {
    const showContacts = shouldShowFsmContacts({lastFree, isNotApplicable});
    if (!showContacts) {
        return true;
    }

    if (!Array.isArray(contacts) || contacts.length < 1 || contacts.length > MAX_FSM_CONTACTS) {
        return false;
    }

    return contacts.every(isContactComplete);
};

export const contactsPayloadForSave = ({lastFree, isNotApplicable, contacts}) => {
    if (!shouldShowFsmContacts({lastFree, isNotApplicable})) {
        return [];
    }

    return contacts.map(({fullName, dateOfBirth, insuranceNumber}) => ({
        fullName,
        dateOfBirth: toFsmContactDateOnly(dateOfBirth),
        insuranceNumber,
    }));
};

export const normalizeFsmContactForCompare = ({fullName, dateOfBirth, insuranceNumber}) => ({
    fullName: fullName || '',
    dateOfBirth: fsmContactDobCalendarDay(dateOfBirth),
    insuranceNumber: insuranceNumber || '',
});
